import { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen } from '@fortawesome/free-solid-svg-icons';
import { getCategoryPhotoObject } from '../../../lib/unsplash';
import UnsplashImage from '../../UnsplashImage';
import ClapButton from '../../shared/ClapButton';

const StoryCard = memo(function StoryCard({ story }) {
  const navigate = useNavigate();
  const [photoObject, setPhotoObject] = useState(story.unsplashPhoto || null);
  const [loading, setLoading] = useState(!story.unsplashPhoto);

  useEffect(() => {
    // If story already has unsplashPhoto, use it
    if (story.unsplashPhoto) {
      setPhotoObject(story.unsplashPhoto);
      setLoading(false);
      return;
    }

    // Otherwise, fetch photo object
    if (story.imageSearchQuery || story.title) {
      setLoading(true);
      getCategoryPhotoObject('stories', story, 0).then(photo => {
        setPhotoObject(photo);
        setLoading(false);
      });
    }
  }, [story]);

  // Calculate reading time based on word count (200 words per minute)
  const getReadingTime = () => {
    if (!story.content) return null;
    const wordCount = story.content.split(/\s+/).filter(word => word.length > 0).length;
    const minutes = Math.ceil(wordCount / 200);
    if (minutes < 1) return '1 min read';
    if (minutes === 1) return '1 min read';
    return `${minutes} min read`;
  };

  const handleCardClick = (e) => {
    // Don't navigate if clicking on clap button
    if (e.target.closest('button')) {
      return;
    }
    navigate(`/explore/story/${story.id}`);
  };

  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className="group bg-white rounded-2xl border border-border overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-primary/10 to-accent/10 overflow-hidden">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
          </div>
        ) : story.unsplashPhoto ? (
          <div className="w-full h-full">
            <UnsplashImage
              photo={story.unsplashPhoto}
              alt={story.title}
              className="group-hover:scale-110 transition-transform duration-500 w-full h-full object-cover"
              onClick={handleCardClick}
              triggerDownloadOnClick={true}
            />
          </div>
        ) : story.uploadedImageUrl ? (
          <div className="relative w-full h-full">
            <img
              src={story.uploadedImageUrl}
              alt={story.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              onClick={handleCardClick}
            />
            {story.imageCaption && (
              <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded text-xs text-white">
                {story.imageCaption}
              </div>
            )}
          </div>
        ) : photoObject ? (
          <div className="w-full h-full">
            <UnsplashImage
              photo={photoObject}
              alt={story.title}
              className="group-hover:scale-110 transition-transform duration-500 w-full h-full object-cover"
              onClick={handleCardClick}
              triggerDownloadOnClick={true}
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FontAwesomeIcon icon={faBookOpen} className="text-6xl text-primary/20" />
          </div>
        )}
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Badge overlay */}
        <div className="absolute top-3 left-3">
          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-text-primary">
            Story
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-serif font-bold text-lg text-text-primary mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {story.title}
        </h3>

        <p className="text-sm text-text-secondary line-clamp-2 mb-4">
          {story.description}
        </p>

        {/* Meta info */}
        {getReadingTime() && (
          <div className="flex items-center gap-4 text-xs text-text-secondary pb-4 border-b border-border">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {getReadingTime()}
            </div>
          </div>
        )}

        {/* Claps and View */}
        <div className="mt-4 flex items-center justify-between">
          {story.id && (
            <ClapButton 
              contentId={story.id} 
              contentType="story" 
              size="sm"
              showCount={true}
            />
          )}
          
          <span className="text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            Read story
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </motion.div>
  );
});

export default StoryCard;

